import type { ReactNode } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import capitalize from "lodash/capitalize";
import { convertToCurrency } from "creso-core/utils";
import type { PlanStatus, STPPlan } from "creso-core/types";
import { AmcLogo } from "../../logos";
import { PlanStatusBadge } from "../../badges";

export type StpColumnOptions = {
  /** Row action menu. Omit it and no action column is produced. */
  renderActions?: (plan: STPPlan) => ReactNode;
};

export function createStpColumns({
  renderActions,
}: StpColumnOptions = {}): ColumnDef<STPPlan>[] {
  const columns: ColumnDef<STPPlan>[] = [
    {
      header: "Scheme name",
      accessorKey: "scheme",
      minSize: 250,
      cell: (info) => (
        <div className="flex flex-row gap-2 items-center">
          <AmcLogo amc_code={info.row.original.amc_morningstar_code} />
          <div className="flex flex-col gap-1">
            <span className="text-sm">
              {info.row.original.switch_in_scheme_name}
            </span>
            <span className="text-xs text-muted-foreground">
              {info.row.original.switch_out_scheme_name}
            </span>
          </div>
        </div>
      ),
    },
    {
      header: "Status",
      accessorKey: "status",
      cell: (info) => (
        <PlanStatusBadge status={info.getValue() as PlanStatus} />
      ),
    },
    {
      header: "Next Installment",
      accessorKey: "installment_due_date",
      cell: (info) => {
        if (!info.getValue()) return "-";
        return <span>{format(info.getValue() as string, "dd MMM yyyy")}</span>;
      },
    },
    {
      header: "Frequency",
      accessorKey: "frequency",
      cell: (info) => (
        <span className="text-sm">{capitalize(info.getValue() as string)}</span>
      ),
    },
    {
      header: "Amount/Units",
      accessorKey: "installment_amount",
      cell: (info) => (
        <span className="text-sm">
          {info.row.original.installment_amount
            ? convertToCurrency(info.getValue() as string)
            : info.row.original.installment_units
              ? `${info.row.original.installment_units} units`
              : "-"}
        </span>
      ),
    },
  ];

  if (renderActions) {
    columns.push({
      id: "actions",
      cell: (info) => (
        <div className="text-right">{renderActions(info.row.original)}</div>
      ),
      minSize: 50,
      size: 50,
    });
  }

  return columns;
}
